/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { posthog } from 'posthog-js';
import {
  getFile,
  updateSpeaker as updateSpeakerRequest,
  askAIV2,
  trim,
  getDownloadUrl,
  getFolders,
  moveFile2,
  deleteFile2,
  shareFile,
  updateFile2,
  reprocessV2,
  deleteAskAISessionV2,
  deleteTemplateOutputV2,
  saveEnhancedNotes,
  SaveEnhancedNotesParams,
  saveNotes,
  SaveNotesParams,
  updateTranscriptSegment,
  restoreTranscriptSegment,
  updateSegmentSpeaker,
  askAIMultimodal,
  bulkUpdateSegmentSpeaker,
  BulkUpdateSegmentSpeakerProps,
  revertToVersion as revertToVersionApi,
} from '../../services/fileSystemService';
import type {
  ChatMessage,
  GetFileReturnType,
  PromptResponses,
  TrimParams,
  UpdateSpeakerProps,
  UpdateSegmentSpeakerProps,
  MediaAnalysisOptions,
} from '../../services/fileSystemService';
import {
  FileType,
  FolderType,
  ShareDetails,
  Speaker,
  requestStatusType,
  CompactSegment,
  SegmentEditRecord,
  Word,
} from '../../types';
import { RootState } from '../../reducers';
import downloadVideo from '../../lib/videoUtils';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import { getShareUrl } from '../../lib/routingUtils';
import { copyToClipboard } from '../../utils/clipboard';
import getTranslation from '../../i18n/utils';
import { DEMO_VIDEO, isDemoVideo } from '../../constants/demoVideo';

interface SuggestionType {
  title: string;
  prompt: string;
}
interface SelectedVideoState {
  status: requestStatusType;
  file: FileType;
  jobs: {
    transcribe?: {
      position: number;
      timeToComplete: number;
      updatedAt: string;
    };
  };
  transcript: {
    compactSegments: CompactSegment[];
    editRecords: Record<string, SegmentEditRecord>;
    history: string[];
    showNotesOnly: boolean;
    updateStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
    words?: Word[];
    selectedSegmentIds: number[];
    selectionMode: boolean;
    selectionModeEntryPoint?: 'dropdown' | 'speaker-name';
    translatedSegments: CompactSegment[];
  };
  chat: {
    status: requestStatusType;
    suggestions: SuggestionType[];
    conversation: ChatMessage[];
    view: 'expanded' | 'compact';
    sessionId: string;
    isVideoMode: boolean;
    ocrEnabled: boolean;
    selectedFiles: string[];
  };
  postProcess: {
    systemPromptResponses: PromptResponses;
    userPromptResponses: PromptResponses;
  };
  actions: {
    trim: {
      status: requestStatusType;
      visibility: boolean;
    };
    share: {
      status: requestStatusType;
    };
    embed: {
      status: requestStatusType;
      url: string;
    };
    download: {
      status: requestStatusType;
    };
    move: {
      status: requestStatusType;
      folders: FolderType[];
    };
    delete: {
      status: requestStatusType;
    };
    clip: {
      status: requestStatusType;
    };
    dub: {
      status: requestStatusType;
    };
    tagUser: {
      status: requestStatusType;
    };
    shareEmail: {
      status: requestStatusType;
    };
    rename: {
      status: requestStatusType;
    };
  };
  summary: {
    status: requestStatusType;
    text: string;
    isEditing: boolean;
  };
  title: {
    status: requestStatusType;
    text: string;
    isEditing: boolean;
  };
  trimmerVisible: boolean;
  clipperVisible: boolean;
}

const initialState: SelectedVideoState = {
  status: 'idle',
  file: {} as FileType,
  jobs: {},
  transcript: {
    words: [],
    compactSegments: [],
    editRecords: {},
    history: [],
    showNotesOnly: false,
    updateStatus: 'idle',
    selectedSegmentIds: [],
    selectionMode: false,
    translatedSegments: [],
  },
  chat: {
    status: 'idle',
    suggestions: [],
    conversation: [],
    view: 'compact',
    sessionId: '',
    isVideoMode: false,
    ocrEnabled: false,
    selectedFiles: [],
  },
  postProcess: {
    systemPromptResponses: {},
    userPromptResponses: {},
  },
  actions: {
    trim: {
      status: 'idle',
      visibility: false,
    },
    share: {
      status: 'idle',
    },
    embed: {
      status: 'idle',
      url: '',
    },
    download: {
      status: 'idle',
    },
    move: {
      status: 'idle',
      folders: [],
    },
    delete: {
      status: 'idle',
    },
    clip: {
      status: 'idle',
    },
    dub: {
      status: 'idle',
    },
    tagUser: {
      status: 'idle',
    },
    shareEmail: {
      status: 'idle',
    },
    rename: {
      status: 'idle',
    },
  },
  summary: {
    status: 'idle',
    text: '',
    isEditing: false,
  },
  title: {
    status: 'idle',
    text: '',
    isEditing: false,
  },
  trimmerVisible: false,
  clipperVisible: false,
};

export const updateSpeaker = createAsyncThunk(
  'selectedVideo/updateSpeaker',
  async (
    data: UpdateSpeakerProps,
  ): Promise<{
    [key: string]: Speaker;
  }> => {
    const speakers = await updateSpeakerRequest(data);
    return speakers;
  },
);

export const updateSegmentSpeakerAction = createAsyncThunk(
  'selectedVideo/updateSegmentSpeaker',
  async (
    data: UpdateSegmentSpeakerProps,
  ): Promise<{
    editRecord: SegmentEditRecord;
    compactSegment: CompactSegment;
  }> => {
    const { editRecord, compactSegment } = await updateSegmentSpeaker(data);
    return { editRecord, compactSegment };
  },
);

export const bulkUpdateSegmentSpeakerAction = createAsyncThunk(
  'selectedVideo/bulkUpdateSegmentSpeaker',
  async (
    data: BulkUpdateSegmentSpeakerProps,
  ): Promise<
    Array<{
      editRecord: SegmentEditRecord;
      compactSegment: CompactSegment;
    }>
  > => {
    const results = await bulkUpdateSegmentSpeaker(data);
    return results;
  },
);

export const reprocessSelectedVideo = createAsyncThunk<
  FileType,
  {
    id: string;
    language?: string;
    feedback?: string;
  },
  { state: RootState; rejectValue: { message: string } }
>(
  'selectedVideo/reprocessSelectedVideo',
  async ({ id, language, feedback }, { getState, rejectWithValue }) => {
    const state = getState();
    const { file } = state.selectedVideo;

    // Track reprocess event
    posthog.capture('reprocess-video', {
      fileId: id,
      fileName: file.name,
      language: language || 'default',
      hasFeedback: !!feedback,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await reprocessV2(id, language, feedback);
      return response.data.file;
    } catch (error: any) {
      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.message ||
          'Failed to reprocess transcript',
      });
    }
  },
);

export const revertToSelectedVersion = createAsyncThunk(
  'selectedVideo/revertToSelectedVersion',
  async ({
    fileId,
    version,
  }: {
    fileId: string;
    version: number;
  }): Promise<
    GetFileReturnType & {
      restoredVersion: number;
      warning?: string;
    }
  > => {
    const response = await revertToVersionApi(fileId, version);
    const refreshed = await getFile(fileId);
    return {
      ...refreshed,
      restoredVersion: response.data.restoredVersion,
      warning: response.data.warning,
    };
  },
);

export const getTeamFolders = createAsyncThunk(
  'folders/getTeamFolders',
  async (teamId: string, { rejectWithValue }) => {
    try {
      const response = await getFolders({ teamId });
      return response.data.data.folders;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export interface MoveFileData {
  fileId: string;
  toTeamId: string;
  toFolderId: string;
}

export const moveFile = createAsyncThunk(
  'files/moveFile',
  async (data: MoveFileData, { rejectWithValue }) => {
    try {
      const response = await moveFile2(data);
      return response.data.data.file;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteFile = createAsyncThunk(
  'files/deleteFile',
  async (fileId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteFile2(fileId);
      if (response.data.success) {
        return response.data.success;
      }
      return null;
    } catch (error) {
      dispatch(
        setAlertMessage({
          type: 'error',
          message: getTranslation('selectedVideo.alerts.deleteFileFailed'),
        }),
      );
      return rejectWithValue(error.message);
    }
  },
);

export const fetchFile = createAsyncThunk(
  'selectedVideo/fetchFile',
  async (fileId: string) => {
    // Handle demo video - return mock data without API call
    if (isDemoVideo(fileId)) {
      return {
        file: DEMO_VIDEO,
        jobs: {},
        transcript: {
          compact_segments: [
            {
              id: 0,
              start: 0,
              end: 18,
              text: 'Upload any file, import existing recordings, or record meetings live, and will transform your recordings into summaries and accurate notes. Find exactly what you need quickly with our fast and accurate summaries. Chat to your recordings and get the answers you need, fast.',
              speaker: '0',
            },
            {
              id: 1,
              start: 21,
              end: 34,
              text: "We'll give you timestamps so you can replay that moment. We automatically summarise meetings, extract key decisions, and identifies action items. Imagine instantly accessing the information you need when you need it.",
              speaker: '0',
            },
            {
              id: 2,
              start: 36,
              end: 47,
              text: 'You can instantly generate sleek, shareable documents from your audio and video files, turn lengthy meetings, webinars, and presentations into concise professional reports, ready to impress with just a prompt.',
              speaker: '0',
            },
            {
              id: 3,
              start: 49,
              end: 57,
              text: "That's just the start. ScreenApp can watch the video frames themselves for you or even join your meetings. Upgrade now to access it all.",
              speaker: '0',
            },
          ],
          edit_records: {},
          words: [],
        },
        chat: {
          conversation: [],
          sessionId: '',
        },
        postProcess: {
          systemPromptResponses: {
            CHAPTERS: {
              promptTitle: 'Chapters',
              responseText: JSON.stringify({
                chapters: [
                  {
                    start: '00:00',
                    end: '00:18',
                    title: 'Upload, Import & Record',
                    notes:
                      'Upload any file, import existing recordings, or record meetings live. ScreenApp transforms your recordings into summaries and accurate notes. Find exactly what you need quickly with fast and accurate summaries.',
                  },
                  {
                    start: '00:21',
                    end: '00:34',
                    title: 'Smart Meeting Analysis',
                    notes:
                      'Get timestamps to replay any moment instantly. ScreenApp automatically summarizes meetings, extracts key decisions, and identifies action items. Imagine instantly accessing the information you need when you need it.',
                  },
                  {
                    start: '00:36',
                    end: '00:47',
                    title: 'Document Generation',
                    notes:
                      'Instantly generate sleek shareable documents from your audio and video files. Turn lengthy meetings, webinars and presentations into concise professional reports, ready to impress with just a prompt.',
                  },
                  {
                    start: '00:49',
                    end: '00:57',
                    title: 'Advanced Features',
                    notes:
                      'ScreenApp can watch the video frames themselves for you or even join your meetings automatically. Upgrade now to access it all and transform how you work with video content.',
                  },
                ],
              }),
            },
            SUMMARY_AND_PROMPTS: {
              promptTitle: 'Summary & Prompts',
              responseText: JSON.stringify({
                prompts: [
                  {
                    title: 'Upload Video',
                    prompt: 'How do I upload my first video?',
                  },
                  {
                    title: 'AI Summaries',
                    prompt: 'How does ScreenApp generate summaries?',
                  },
                  {
                    title: 'Share Recordings',
                    prompt: 'Can I share my recordings with my team?',
                  },
                  {
                    title: 'Key Features',
                    prompt: 'What are the main features of ScreenApp?',
                  },
                ],
              }),
            },
          },
          userPromptResponses: {},
        },
      };
    }

    const data = await getFile(fileId);
    return data;
  },
);

export const fetchFileUpdate = createAsyncThunk(
  'selectedVideo/fetchFileUpdate',
  async (fileId: string) => {
    // Handle demo video - return mock data without API call
    if (isDemoVideo(fileId)) {
      return {
        file: DEMO_VIDEO,
        jobs: {},
        transcript: {
          compact_segments: [
            {
              id: 0,
              start: 0,
              end: 18,
              text: 'Upload any file, import existing recordings, or record meetings live, and will transform your recordings into summaries and accurate notes. Find exactly what you need quickly with our fast and accurate summaries. Chat to your recordings and get the answers you need, fast.',
              speaker: '0',
            },
            {
              id: 1,
              start: 21,
              end: 34,
              text: "We'll give you timestamps so you can replay that moment. We automatically summarise meetings, extract key decisions, and identifies action items. Imagine instantly accessing the information you need when you need it.",
              speaker: '0',
            },
            {
              id: 2,
              start: 36,
              end: 47,
              text: 'You can instantly generate sleek, shareable documents from your audio and video files, turn lengthy meetings, webinars, and presentations into concise professional reports, ready to impress with just a prompt.',
              speaker: '0',
            },
            {
              id: 3,
              start: 49,
              end: 57,
              text: "That's just the start. ScreenApp can watch the video frames themselves for you or even join your meetings. Upgrade now to access it all.",
              speaker: '0',
            },
          ],
          edit_records: {},
          words: [],
        },
        chat: {
          conversation: [],
          sessionId: '',
        },
        postProcess: {
          systemPromptResponses: {
            CHAPTERS: {
              promptTitle: 'Chapters',
              responseText: JSON.stringify({
                chapters: [
                  {
                    start: '00:00',
                    end: '00:18',
                    title: 'Upload, Import & Record',
                    notes:
                      'Upload any file, import existing recordings, or record meetings live. ScreenApp transforms your recordings into summaries and accurate notes. Find exactly what you need quickly with fast and accurate summaries.',
                  },
                  {
                    start: '00:21',
                    end: '00:34',
                    title: 'Smart Meeting Analysis',
                    notes:
                      'Get timestamps to replay any moment instantly. ScreenApp automatically summarizes meetings, extracts key decisions, and identifies action items. Imagine instantly accessing the information you need when you need it.',
                  },
                  {
                    start: '00:36',
                    end: '00:47',
                    title: 'Document Generation',
                    notes:
                      'Instantly generate sleek shareable documents from your audio and video files. Turn lengthy meetings, webinars and presentations into concise professional reports, ready to impress with just a prompt.',
                  },
                  {
                    start: '00:49',
                    end: '00:57',
                    title: 'Advanced Features',
                    notes:
                      'ScreenApp can watch the video frames themselves for you or even join your meetings automatically. Upgrade now to access it all and transform how you work with video content.',
                  },
                ],
              }),
            },
            SUMMARY_AND_PROMPTS: {
              promptTitle: 'Summary & Prompts',
              responseText: JSON.stringify({
                prompts: [
                  {
                    title: 'Upload Video',
                    prompt: 'How do I upload my first video?',
                  },
                  {
                    title: 'AI Summaries',
                    prompt: 'How does ScreenApp generate summaries?',
                  },
                  {
                    title: 'Share Recordings',
                    prompt: 'Can I share my recordings with my team?',
                  },
                  {
                    title: 'Key Features',
                    prompt: 'What are the main features of ScreenApp?',
                  },
                ],
              }),
            },
          },
          userPromptResponses: {},
        },
      };
    }

    const data = await getFile(fileId);
    return data;
  },
);

interface UpdateFileOptions {
  fileId: string;
  summary: string;
}

export const updateSummary = createAsyncThunk(
  'selectedVideo/summary/update',
  async (options: UpdateFileOptions): Promise<string> => {
    const { fileId, summary } = options;
    const response = await updateFile2(fileId, { description: summary });
    const description = response?.data?.data?.file?.description || '';
    return description;
  },
);

interface UpdateNameOptions {
  fileId: string;
  title: string;
}

export const updateTitle = createAsyncThunk(
  'selectedVideo/title/update',
  async (options: UpdateNameOptions, { dispatch }): Promise<string> => {
    try {
      const { fileId, title } = options;
      const response = await updateFile2(fileId, { name: title });
      const updatedName = response?.data?.data?.file?.name || '';

      // Update the library state with the new name
      dispatch({
        type: 'library/updateFile',
        payload: { _id: fileId, name: updatedName },
      });

      return updatedName;
    } catch (error) {
      dispatch(
        setAlertMessage({
          type: 'error',
          message: getTranslation('selectedVideo.alerts.updateTitleFailed'),
        }),
      );
      throw error;
    }
  },
);

export const trimFile = createAsyncThunk<
  { fileId: string; status: 'processing' },
  Pick<TrimParams, 'startMS' | 'endMS'>
>('selectedVideo/trim', async ({ startMS, endMS }, { getState }) => {
  const { selectedVideo } = getState() as RootState;
  const { _id: fileId } = selectedVideo.file;
  const response = await trim({
    fileId,
    startMS,
    endMS,
  });
  return response.data.data;
});

interface AskQuestionOptions {
  fileId: string;
  question: string;
  sessionId?: string;
}

export const askQuestion = createAsyncThunk(
  'selectedVideo/askQuestion',
  async ({ fileId, question, sessionId }: AskQuestionOptions) => {
    // Handle demo video - return mock AI response
    if (isDemoVideo(fileId)) {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return {
        answer: {
          role: 'assistant' as const,
          content: `To help you save your AI credits, Ask AI is disabled for this demo video. Instead, put in a prompt under the video to see ScreenApp's AI in action and create professional documents.`,
        },
        sessionId: 'demo-session',
      };
    }

    const response = await askAIV2({
      fileId,
      question,
      sessionId,
    });
    return response.data.data;
  },
);

interface AskMultimodalQuestionOptions {
  fileId: string;
  promptText: string;
  mediaAnalysisOptions: MediaAnalysisOptions;
  sessionId?: string;
}

export const askMultimodalQuestion = createAsyncThunk(
  'selectedVideo/askQuestion/multimodal',
  async ({
    fileId,
    promptText,
    mediaAnalysisOptions,
    sessionId,
  }: AskMultimodalQuestionOptions) => {
    // Handle demo video - return mock AI response
    if (isDemoVideo(fileId)) {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      return {
        answer: {
          role: 'assistant' as const,
          content: `This is a demo video analysis response. The ScreenApp Demo video shows various product features and demonstrations through visual content, including UI demonstrations, feature walkthroughs, and product capabilities.`,
        },
        sessionId: 'demo-session',
      };
    }

    const response = await askAIMultimodal({
      fileId,
      promptText,
      mediaAnalysisOptions,
      sessionId,
    });
    return response.data.data;
  },
);

export const deleteAskAISession = createAsyncThunk(
  'selectedVideo/deleteAskAISession',
  async ({ fileId, sessionId }: { fileId: string; sessionId: string }) => {
    const response = await deleteAskAISessionV2(fileId, sessionId);
    return response.data.success;
  },
);

export const deleteTemplateOutput = createAsyncThunk(
  'selectedVideo/deleteTemplateOutput',
  async ({ fileId, templateId }: { fileId: string; templateId: string }) => {
    const response = await deleteTemplateOutputV2(fileId, templateId);
    return !!response.data.success;
  },
);

export const shareThunk = createAsyncThunk(
  'selectedVideo/share',
  async (
    options: Parameters<typeof shareFile>[0] & {
      copyUrlToClipboard?: boolean;
      existingShareId?: string;
    },
    { getState },
  ): Promise<ShareDetails> => {
    const {
      fileId,
      permissions,
      copyUrlToClipboard = true,
      existingShareId: providedShareId,
    } = options;
    const { selectedVideo } = getState() as RootState;

    // Use provided shareId if available, otherwise check if this is the currently selected video
    const isSelectedVideo = selectedVideo.file._id === fileId;
    const existingShareId =
      providedShareId ||
      (isSelectedVideo ? selectedVideo.file.share?.shareId : undefined);

    let shareId = existingShareId || '';
    if (!existingShareId) {
      const { shareId: newShareId } = await shareFile({
        fileId,
        permissions,
      });
      shareId = newShareId || '';
    }
    // Copy the URL to clipboard with iOS Safari compatibility
    if (copyUrlToClipboard && shareId) {
      const url = getShareUrl(shareId);
      const result = await copyToClipboard(url);

      if (!result.success) {
        console.warn('Failed to copy to clipboard:', result.error);
        // Don't reject the thunk, just log the warning
        // The share operation itself was successful
      }
    }
    return { shareId };
  },
);

export const downloadFile = createAsyncThunk(
  'selectedVideo/download',
  async (data: Parameters<typeof getDownloadUrl>[0], { rejectWithValue }) => {
    try {
      const { url } = await getDownloadUrl(data);
      if (url) {
        downloadVideo(url, data.fileId);
      }
      return { url };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  },
);

export const saveEnhancedNotesThunk = createAsyncThunk(
  'selectedVideo/saveEnhancedNotes',
  async (params: SaveEnhancedNotesParams) => {
    const response = await saveEnhancedNotes(params);
    return {
      success: response.data.success,
      responseText: params.responseText,
    };
  },
);

export const saveNotesThunk = createAsyncThunk(
  'selectedVideo/saveNotes',
  async ({ fileId, notes }: SaveNotesParams) => {
    const response = await saveNotes({ fileId, notes });
    return {
      success: response.data.success,
      notes,
    };
  },
);

export const setClipperVisibility = createAction<boolean>(
  'selectedVideo/setClipperVisibility',
);

export const updateSegmentText = createAsyncThunk(
  'selectedVideo/updateSegmentText',
  async (
    {
      segmentId,
      newText,
    }: {
      segmentId: string | number;
      newText: string;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    const { file } = state.selectedVideo;

    const { editRecord, compactSegment } = await updateTranscriptSegment({
      fileId: file._id,
      segmentId,
      newText,
      teamId: file.teamId,
    });

    return {
      segmentId,
      editRecord,
      compactSegment,
    };
  },
);

export const restoreSegmentVersion = createAsyncThunk(
  'selectedVideo/restoreSegmentVersion',
  async (
    {
      segmentId,
      versionId,
    }: {
      segmentId: string | number;
      versionId: string;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    const { file } = state.selectedVideo;

    const { editRecord, compactSegment } = await restoreTranscriptSegment({
      fileId: file._id,
      segmentId,
      versionId,
      teamId: file.teamId,
    });

    return {
      segmentId,
      editRecord,
      compactSegment,
    };
  },
);

const selectedVideoSlice = createSlice({
  name: 'selectedVideo',
  initialState,
  reducers: {
    selectVideoFromLibrary: (state, action: PayloadAction<FileType>) => {
      // Pre-populate file data from library cache for instant UI rendering
      state.file = action.payload;
      // Set status to succeeded so UI doesn't show loader
      state.status = 'succeeded';
      // Set title and summary from cached data
      state.title.text = action.payload.name || '';
      state.summary.text = action.payload.description || '';
    },
    // Trimmer
    setTrimmerVisibility: (state, action: PayloadAction<boolean>) => {
      state.actions.trim.visibility = action.payload;
    },
    setTrimStatus: (state, action: PayloadAction<requestStatusType>) => {
      state.actions.trim.status = action.payload;
    },
    // TODO: remove
    resetDownloadUrl: (state) => {
      state.actions.download.status = 'idle';
    },
    // Summary
    toggleSummaryEdit: (state) => {
      state.summary.isEditing = !state.summary.isEditing;
    },
    toggleChatView: (state) => {
      state.chat.view = state.chat.view === 'expanded' ? 'compact' : 'expanded';
    },
    cleanup: () => initialState,
    updateJobStatus: (
      state,
      action: PayloadAction<{
        position: number;
        timeToComplete: number;
        updatedAt: string;
        job: 'transcribe';
      }>,
    ) => {
      const job = action.payload;
      state.jobs[job.job] = job;
    },
    setTemplateOutput: (
      state,
      action: PayloadAction<{
        templateId: string;
        promptTitle: string;
        responseText: string;
      }>,
    ) => {
      const { templateId, responseText, promptTitle } = action.payload;
      state.postProcess.userPromptResponses[templateId] = {
        promptTitle,
        responseText,
      };
    },
    setShowNotesOnly: (state, action: PayloadAction<boolean>) => {
      state.transcript.showNotesOnly = action.payload;
    },
    setVideoMode: (state, action: PayloadAction<boolean>) => {
      state.chat.isVideoMode = action.payload;
      // Disable OCR and clear selected files when enabling video mode
      if (action.payload) {
        state.chat.ocrEnabled = false;
        state.chat.selectedFiles = [];
      }
    },
    setOcrMode: (state, action: PayloadAction<boolean>) => {
      state.chat.ocrEnabled = action.payload;
      // Disable video mode and clear selected files when enabling OCR
      if (action.payload) {
        state.chat.isVideoMode = false;
        state.chat.selectedFiles = [];
      }
    },
    addSelectedFile: (state, action: PayloadAction<string>) => {
      const fileId = action.payload;
      if (!state.chat.selectedFiles.includes(fileId)) {
        state.chat.selectedFiles.push(fileId);
        // Disable video mode and OCR when adding files
        state.chat.isVideoMode = false;
        state.chat.ocrEnabled = false;
      }
    },
    removeSelectedFile: (state, action: PayloadAction<string>) => {
      state.chat.selectedFiles = state.chat.selectedFiles.filter(
        (id) => id !== action.payload,
      );
    },
    clearSelectedFiles: (state) => {
      state.chat.selectedFiles = [];
    },
    clearChatError: (state) => {
      if (state.chat.status === 'failed') {
        state.chat.status = 'idle';
      }
    },
    clearChatConversation: (state) => {
      state.chat.sessionId = '';
      state.chat.conversation = [];
    },
    // Multi-file chat actions
    addUserMessageToChat: (state, action: PayloadAction<ChatMessage>) => {
      state.chat.conversation.push(action.payload);
    },
    addAssistantMessageToChat: (state, action: PayloadAction<ChatMessage>) => {
      state.chat.conversation.push(action.payload);
    },
    updateLastAssistantMessage: (state, action: PayloadAction<string>) => {
      const lastIndex = state.chat.conversation.length - 1;
      if (
        lastIndex >= 0 &&
        state.chat.conversation[lastIndex].role === 'assistant'
      ) {
        state.chat.conversation[lastIndex].content = action.payload;
      }
    },
    setChatStatus: (state, action: PayloadAction<requestStatusType>) => {
      state.chat.status = action.payload;
    },
    setChatSessionId: (state, action: PayloadAction<string>) => {
      state.chat.sessionId = action.payload;
    },
    truncateConversation: (state, action: PayloadAction<number>) => {
      state.chat.conversation = state.chat.conversation.slice(
        0,
        action.payload,
      );
    },
    resetSelectedVideo: () => initialState,
    toggleSelectionMode: (
      state,
      action: PayloadAction<'dropdown' | 'speaker-name' | undefined>,
    ) => {
      state.transcript.selectionMode = !state.transcript.selectionMode;
      if (state.transcript.selectionMode) {
        // Store the entry point when entering selection mode
        state.transcript.selectionModeEntryPoint = action.payload || 'dropdown';
      } else {
        // Clear the entry point and selected segments when exiting
        state.transcript.selectedSegmentIds = [];
        state.transcript.selectionModeEntryPoint = undefined;
      }
    },
    toggleSegmentSelection: (state, action: PayloadAction<number>) => {
      const segmentId = action.payload;
      const index = state.transcript.selectedSegmentIds.indexOf(segmentId);
      if (index > -1) {
        state.transcript.selectedSegmentIds.splice(index, 1);
        // Exit selection mode if no segments are selected
        if (state.transcript.selectedSegmentIds.length === 0) {
          state.transcript.selectionMode = false;
          state.transcript.selectionModeEntryPoint = undefined;
        }
      } else {
        state.transcript.selectedSegmentIds.push(segmentId);
      }
    },
    clearSegmentSelection: (state) => {
      state.transcript.selectedSegmentIds = [];
    },
    selectAllSegments: (state) => {
      state.transcript.selectedSegmentIds =
        state.transcript.compactSegments.map((segment) => segment.id);
    },
    setFileTag: (
      state,
      action: PayloadAction<{ key: string; value: string }>,
    ) => {
      if (!state.file.tags) {
        state.file.tags = {};
      }
      state.file.tags[action.payload.key] = action.payload.value;
    },
    setTranslatedSegments: (state, action: PayloadAction<CompactSegment[]>) => {
      state.transcript.translatedSegments = action.payload;
    },
    updateFileDataWithoutLoading: (
      state,
      action: PayloadAction<{
        file: FileType;
        jobs?: any;
        transcript?: any;
        chat?: any;
        postProcess?: any;
      }>,
    ) => {
      // Update file data without changing the loading status
      const { file, jobs, transcript, chat, postProcess } = action.payload;

      // Update file data
      if (file) {
        state.file = { ...state.file, ...file };
        state.summary.text = file.description || state.summary.text;
        state.title.text = file.name || state.title.text;
      }

      // Update jobs if provided
      if (jobs) {
        state.jobs = jobs;
      }

      // Update transcript if provided
      if (transcript) {
        const {
          compact_segments: compactSegments,
          edit_records: editRecords,
          words,
        } = transcript;

        if (words) {
          state.transcript.words = words;
        }

        if (compactSegments) {
          state.transcript.compactSegments = compactSegments.map(
            (segment: any) => ({
              ...segment,
              isEdited: false,
            }),
          );
        }

        if (editRecords) {
          state.transcript.editRecords = editRecords;
        }
      }

      // Update chat if provided
      if (chat) {
        if (chat.conversation) {
          state.chat.conversation = chat.conversation;
        }
        if (chat.sessionId) {
          state.chat.sessionId = chat.sessionId;
        }
      }

      // Update postProcess if provided
      if (postProcess) {
        const { systemPromptResponses, userPromptResponses } = postProcess;

        if (systemPromptResponses) {
          state.postProcess.systemPromptResponses = systemPromptResponses;

          // Update suggestions from SUMMARY_AND_PROMPTS
          try {
            const { responseText } =
              systemPromptResponses?.SUMMARY_AND_PROMPTS || {
                responseText: '',
              };
            const summaryAndPrompt = JSON.parse(responseText) as {
              prompts?: SuggestionType[];
            };
            state.chat.suggestions = summaryAndPrompt?.prompts || [];
          } catch (error) {
            // Keep existing suggestions if parsing fails
            console.error('Error parsing suggestions during update', error);
          }
        }

        if (userPromptResponses) {
          state.postProcess.userPromptResponses = Object.fromEntries(
            Object.entries(userPromptResponses).map(
              ([id, response]: [string, any]) => [
                id,
                {
                  responseText: response.responseText,
                  promptTitle: response.promptTitle || 'AI notes',
                },
              ],
            ),
          );
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch File
    builder.addCase(fetchFile.pending, (state, action) => {
      // Only set to pending if we don't already have cached data for this file
      // This prevents showing loader when navigating from library with cached data
      const fileId = action.meta.arg;
      const hasCachedData =
        state.file._id === fileId && state.status === 'succeeded';
      if (!hasCachedData) {
        state.status = 'pending';
      }
    });
    builder.addCase(fetchFile.fulfilled, (state, action) => {
      const { file, jobs, transcript, chat, postProcess } = action.payload;
      const { systemPromptResponses, userPromptResponses } = postProcess || {};
      const {
        compact_segments: compactSegments,
        edit_records: editRecords,
        words,
      } = transcript || {};
      state.status = 'succeeded';
      state.file = file;
      state.jobs = jobs;
      state.chat.conversation = chat.conversation;
      state.chat.sessionId = chat.sessionId;
      state.transcript.words = words || [];
      state.transcript.compactSegments = (compactSegments || []).map(
        (segment) => ({
          ...segment,
          isEdited: false,
        }),
      );
      state.transcript.editRecords = editRecords || {};
      state.summary.text = file.description || '';
      state.title.text = file.name || '';
      state.postProcess.systemPromptResponses = systemPromptResponses;
      state.postProcess.userPromptResponses = Object.fromEntries(
        Object.entries(userPromptResponses).map(([id, response]) => [
          id,
          {
            responseText: response.responseText,
            promptTitle: response.promptTitle || 'AI notes',
          },
        ]),
      );
      // Suggestions are in `SUMMARY_AND_PROMPTS` in systemPromptResponses
      try {
        const summaryAndPrompts = systemPromptResponses as any;
        const { responseText } = summaryAndPrompts?.SUMMARY_AND_PROMPTS || {
          responseText: '',
        };
        if (!responseText || responseText.trim() === '') {
          state.chat.suggestions = [];
          return;
        }
        const summaryAndPrompt = JSON.parse(responseText) as {
          prompts?: SuggestionType[];
        };
        state.chat.suggestions = summaryAndPrompt?.prompts || [];
      } catch (error) {
        state.chat.suggestions = [];
        console.error('Error parsing suggestions', error);
      }
    });
    builder.addCase(fetchFile.rejected, (state) => {
      state.status = 'failed';
    });
    // Fetch File Update (without changing loading state)
    builder.addCase(fetchFileUpdate.fulfilled, (state, action) => {
      const { file, jobs, transcript, chat, postProcess } = action.payload;
      const { systemPromptResponses, userPromptResponses } = postProcess || {};
      const {
        compact_segments: compactSegments,
        edit_records: editRecords,
        words,
      } = transcript || {};

      // Update file data without changing status
      state.file = file;
      state.jobs = jobs;
      state.chat.conversation = chat.conversation;
      state.chat.sessionId = chat.sessionId;
      state.transcript.words = words || [];
      state.transcript.compactSegments = (compactSegments || []).map(
        (segment) => ({
          ...segment,
          isEdited: false,
        }),
      );
      state.transcript.editRecords = editRecords || {};
      state.summary.text = file.description || '';
      state.title.text = file.name || '';
      state.postProcess.systemPromptResponses = systemPromptResponses;
      state.postProcess.userPromptResponses = Object.fromEntries(
        Object.entries(userPromptResponses).map(([id, response]) => [
          id,
          {
            responseText: response.responseText,
            promptTitle: response.promptTitle || 'AI notes',
          },
        ]),
      );
      // Suggestions are in `SUMMARY_AND_PROMPTS` in systemPromptResponses
      try {
        const summaryAndPrompts = systemPromptResponses as any;
        const { responseText } = summaryAndPrompts?.SUMMARY_AND_PROMPTS || {
          responseText: '',
        };
        if (!responseText || responseText.trim() === '') {
          state.chat.suggestions = [];
          return;
        }
        const summaryAndPrompt = JSON.parse(responseText) as {
          prompts?: SuggestionType[];
        };
        state.chat.suggestions = summaryAndPrompt?.prompts || [];
      } catch (error) {
        state.chat.suggestions = [];
        console.error('Error parsing suggestions', error);
      }
    });
    // Delete Template Output
    builder.addCase(deleteTemplateOutput.fulfilled, (state, action) => {
      const { templateId, fileId } = action.meta.arg;
      if (fileId === state.file._id) {
        delete state.postProcess.userPromptResponses[templateId];
      }
    });
    // Update Speaker
    builder.addCase(updateSpeaker.pending, (state, action) => {
      const payload = action.meta.arg;
      if (!state.file?.textData?.speakers) {
        state.file.textData = {
          ...state.file.textData,
          speakers: {},
        };
      }
      if (!state.file?.textData?.speakers) return;
      const { speakerId, speaker } = payload;
      const currentSpeaker = state.file.textData.speakers[speakerId];
      state.file.textData = {
        ...state.file.textData,
        speakers: {
          ...state.file?.textData?.speakers,
          [speakerId]: {
            ...currentSpeaker,
            ...speaker,
          },
        },
      };
    });
    builder.addCase(reprocessSelectedVideo.fulfilled, (state, action) => {
      state.file = {
        ...state.file,
        ...action.payload,
      };

      if (
        action.payload.textData?.transcriptRequestedAt &&
        !action.payload.textData?.transcriptUrl &&
        !state.jobs.transcribe
      ) {
        state.jobs.transcribe = {
          position: 0,
          timeToComplete: 0,
          updatedAt: new Date().toISOString(),
        };
      }
    });
    builder.addCase(reprocessSelectedVideo.rejected, (state) => {
      if (
        state.file.textData?.transcriptRequestedAt &&
        !state.file.textData?.transcriptUrl &&
        !state.file.textData?.transcriptCompletedAt
      ) {
        state.file.textData.transcriptRequestedAt = undefined;
      }

      state.jobs.transcribe = undefined;
    });
    // Revert to previous version
    builder.addCase(revertToSelectedVersion.fulfilled, (state, action) => {
      const { file, jobs, transcript, chat, postProcess } = action.payload;
      const { systemPromptResponses, userPromptResponses } = postProcess || {};
      const {
        compact_segments: compactSegments,
        edit_records: editRecords,
        words,
      } = transcript || {};

      state.file = file;
      state.jobs = jobs;
      state.chat.conversation = chat?.conversation || [];
      state.chat.sessionId = chat?.sessionId || '';
      state.transcript.words = words || [];
      state.transcript.compactSegments = (compactSegments || []).map(
        (segment) => ({
          ...segment,
          isEdited: false,
        }),
      );
      state.transcript.editRecords = editRecords || {};
      state.summary.text = file.description || '';
      state.title.text = file.name || '';
      state.postProcess.systemPromptResponses = systemPromptResponses;
      state.postProcess.userPromptResponses = Object.fromEntries(
        Object.entries(userPromptResponses || {}).map(([id, response]) => [
          id,
          {
            responseText: response.responseText,
            promptTitle: response.promptTitle || 'AI notes',
          },
        ]),
      );
      try {
        const summaryAndPrompts = systemPromptResponses as any;
        const { responseText } = summaryAndPrompts?.SUMMARY_AND_PROMPTS || {
          responseText: '',
        };
        if (!responseText || responseText.trim() === '') {
          state.chat.suggestions = [];
          return;
        }
        const summaryAndPrompt = JSON.parse(responseText) as {
          prompts?: SuggestionType[];
        };
        state.chat.suggestions = summaryAndPrompt?.prompts || [];
      } catch (error) {
        state.chat.suggestions = [];
        console.error('Error parsing suggestions', error);
      }
    });
    // Chat
    builder.addCase(askQuestion.pending, (state, action) => {
      const { question } = action.meta.arg;
      state.chat.conversation.push({
        role: 'user',
        content: question,
      } as ChatMessage);
      state.chat.status = 'pending';
    });
    builder.addCase(askQuestion.fulfilled, (state, action) => {
      state.chat.status = 'succeeded';
      state.chat.sessionId = action.payload.sessionId;
      state.chat.conversation.push(action.payload.answer);
    });
    builder.addCase(askQuestion.rejected, (state) => {
      state.chat.status = 'failed';
    });
    // Multimodal Chat
    builder.addCase(askMultimodalQuestion.pending, (state, action) => {
      const { promptText } = action.meta.arg;
      state.chat.conversation.push({
        role: 'user',
        content: promptText,
      } as ChatMessage);
      state.chat.status = 'pending';
    });
    builder.addCase(askMultimodalQuestion.fulfilled, (state, action) => {
      state.chat.status = 'succeeded';
      state.chat.sessionId = action.payload.sessionId;
      state.chat.conversation.push(action.payload.answer);
    });
    builder.addCase(askMultimodalQuestion.rejected, (state) => {
      state.chat.status = 'failed';
    });
    // Delete Ask AI Session
    builder.addCase(deleteAskAISession.fulfilled, (state) => {
      state.chat.sessionId = '';
      state.chat.conversation = [];
    });
    // Share
    builder.addCase(shareThunk.pending, (state) => {
      state.actions.share.status = 'pending';
    });
    builder.addCase(shareThunk.fulfilled, (state, action) => {
      state.actions.share.status = 'succeeded';
      const { shareId } = action.payload;
      state.file.share = {
        ...state.file.share,
        shareId,
      };
    });
    builder.addCase(shareThunk.rejected, (state) => {
      state.actions.share.status = 'failed';
    });
    // Download
    builder.addCase(downloadFile.pending, (state) => {
      state.actions.download.status = 'pending';
    });
    builder.addCase(downloadFile.fulfilled, (state) => {
      state.actions.download.status = 'succeeded';
    });
    builder.addCase(downloadFile.rejected, (state) => {
      state.actions.download.status = 'failed';
    });
    // Get team folders
    builder.addCase(getTeamFolders.pending, (state) => {
      state.actions.move.status = 'pending';
    });
    builder.addCase(
      getTeamFolders.fulfilled,
      (state, action: PayloadAction<FolderType[]>) => {
        state.actions.move.status = 'succeeded';
        state.actions.move.folders = action.payload;
      },
    );
    builder.addCase(getTeamFolders.rejected, (state) => {
      state.actions.move.status = 'failed';
    });
    // Move
    builder.addCase(moveFile.pending, (state) => {
      state.actions.move.status = 'pending';
    });
    builder.addCase(
      moveFile.fulfilled,
      (state, action: PayloadAction<FileType>) => {
        state.actions.move.status = 'succeeded';
        state.file = action.payload;
      },
    );
    builder.addCase(moveFile.rejected, (state) => {
      state.actions.move.status = 'failed';
    });
    // Delete
    builder.addCase(deleteFile.pending, (state) => {
      state.actions.delete.status = 'pending';
    });
    builder.addCase(deleteFile.fulfilled, (state) => {
      state.actions.delete.status = 'succeeded';
    });
    builder.addCase(deleteFile.rejected, (state) => {
      state.actions.delete.status = 'failed';
    });
    // Update Summary
    builder.addCase(updateSummary.pending, (state) => {
      state.summary.status = 'pending';
    });
    builder.addCase(updateSummary.fulfilled, (state, action) => {
      const text = action.payload || '';
      state.summary.text = text;
      state.file.description = text;
      state.summary.status = 'succeeded';
      state.summary.isEditing = false;
    });
    builder.addCase(updateSummary.rejected, (state) => {
      state.summary.status = 'failed';
      state.summary.text = state.file.description || '';
    });
    // Update Title
    builder.addCase(updateTitle.pending, (state) => {
      state.title.status = 'pending';
    });
    builder.addCase(updateTitle.fulfilled, (state, action) => {
      const text = action.payload || '';
      state.title.text = text;
      state.file.name = text;
      state.title.status = 'succeeded';
    });
    builder.addCase(updateTitle.rejected, (state) => {
      state.title.status = 'failed';
      state.title.text = state.file.name || '';
    });
    builder.addCase(trimFile.pending, (state) => {
      state.actions.trim.status = 'pending';
      // Keep dialog open - socket notification will close it when trim completes
    });
    builder.addCase(trimFile.fulfilled, () => {
      // API now returns { status: 'processing' } - keep pending until socket notification
      // Socket update will set status to 'succeeded' when trim completes
    });
    builder.addCase(trimFile.rejected, (state) => {
      state.actions.trim.status = 'failed';
    });
    // Save Enhanced Notes
    builder.addCase(saveEnhancedNotesThunk.fulfilled, (state, action) => {
      const { responseText } = action.payload;
      state.postProcess.systemPromptResponses.ENHANCED_NOTES = {
        promptTitle: 'Notes',
        responseText,
      };
    });
    // Save Notes
    builder.addCase(saveNotesThunk.fulfilled, (state, action) => {
      if (!state.file.textData) {
        state.file.textData = {};
      }
      state.file.textData.notes = action.payload.notes;
    });
    builder.addCase(setClipperVisibility, (state, action) => {
      state.clipperVisible = action.payload;
    });
    builder.addCase(updateSegmentText.fulfilled, (state, action) => {
      const { segmentId, editRecord, compactSegment } = action.payload;
      console.log('updateSegmentText', action.payload);
      const segmentIndex = state.transcript?.compactSegments.findIndex(
        (s: CompactSegment) => +s.id === +segmentId,
      );

      if (segmentIndex === -1) {
        return;
      }

      state.transcript.compactSegments[segmentIndex] = compactSegment;

      if (!state.transcript.history) {
        state.transcript.history = [];
      }
      state.transcript.history.push(editRecord.id);

      if (!state.transcript.editRecords) {
        state.transcript.editRecords = {};
      }
      state.transcript.editRecords[editRecord.id] = editRecord;
    });
    builder.addCase(restoreSegmentVersion.fulfilled, (state, action) => {
      const { segmentId, editRecord, compactSegment } = action.payload;
      const segmentIndex = state.transcript?.compactSegments.findIndex(
        (s: CompactSegment) => +s.id === +segmentId,
      );

      if (segmentIndex === -1) {
        return;
      }

      state.transcript.compactSegments[segmentIndex] = compactSegment;

      if (!state.transcript.history) {
        state.transcript.history = [];
      }
      state.transcript.history.push(editRecord.id);

      if (!state.transcript.editRecords) {
        state.transcript.editRecords = {};
      }
      state.transcript.editRecords[editRecord.id] = editRecord;
    });
    builder.addCase(updateSegmentSpeakerAction.fulfilled, (state, action) => {
      const { editRecord, compactSegment } = action.payload;
      const segmentIndex = state.transcript?.compactSegments.findIndex(
        (s: CompactSegment) => +s.id === +compactSegment.id,
      );

      if (segmentIndex === -1) {
        return;
      }

      // Create a new compactSegments array to ensure immutability and trigger re-renders
      state.transcript.compactSegments = state.transcript.compactSegments.map(
        (seg, idx) => (idx === segmentIndex ? compactSegment : seg),
      );

      // Ensure the new speaker exists in the speakers mapping
      if (compactSegment.speaker) {
        if (!state.file?.textData) {
          state.file.textData = {};
        }
        if (!state.file.textData.speakers) {
          state.file.textData.speakers = {};
        }

        // Only create speaker entry if it doesn't exist
        if (!state.file.textData.speakers[compactSegment.speaker]) {
          // Create a new speakers object to ensure immutability
          const newSpeakers = {
            ...state.file.textData.speakers,
            [compactSegment.speaker]: {
              name: compactSegment.speaker,
              userId: '',
              picture: '',
              email: '',
            },
          };

          // Update textData to trigger re-render
          state.file.textData = {
            ...state.file.textData,
            speakers: newSpeakers,
          };
        }
      }

      if (!state.transcript.history) {
        state.transcript.history = [];
      }
      state.transcript.history.push(editRecord.id);

      if (!state.transcript.editRecords) {
        state.transcript.editRecords = {};
      }
      state.transcript.editRecords[editRecord.id] = editRecord;
    });
    builder.addCase(
      bulkUpdateSegmentSpeakerAction.fulfilled,
      (state, action) => {
        // Create a map of updated segments for efficient lookup
        const updatedSegmentsMap = new Map(
          action.payload.map(({ compactSegment }) => [
            +compactSegment.id,
            compactSegment,
          ]),
        );

        // Update all segments in a single pass
        state.transcript.compactSegments = state.transcript.compactSegments.map(
          (seg) => {
            const updatedSegment = updatedSegmentsMap.get(+seg.id);
            return updatedSegment || seg;
          },
        );

        // Ensure all new speakers exist in the speakers mapping
        action.payload.forEach(({ compactSegment }) => {
          if (compactSegment.speaker) {
            if (!state.file?.textData) {
              state.file.textData = {};
            }
            if (!state.file.textData.speakers) {
              state.file.textData.speakers = {};
            }

            // Only create speaker entry if it doesn't exist
            if (!state.file.textData.speakers[compactSegment.speaker]) {
              state.file.textData.speakers = {
                ...state.file.textData.speakers,
                [compactSegment.speaker]: {
                  name: compactSegment.speaker,
                  userId: '',
                  picture: '',
                  email: '',
                },
              };
            }
          }
        });

        // Update textData to trigger re-render if speakers were modified
        if (state.file?.textData) {
          state.file.textData = {
            ...state.file.textData,
            speakers: { ...state.file.textData.speakers },
          };
        }

        // Add all edit records to history
        action.payload.forEach(({ editRecord }) => {
          if (!state.transcript.history) {
            state.transcript.history = [];
          }
          state.transcript.history.push(editRecord.id);

          if (!state.transcript.editRecords) {
            state.transcript.editRecords = {};
          }
          state.transcript.editRecords[editRecord.id] = editRecord;
        });

        // Clear selection after bulk update
        state.transcript.selectedSegmentIds = [];
        state.transcript.selectionMode = false;
      },
    );
  },
});

export const {
  selectVideoFromLibrary,
  cleanup,
  setTrimmerVisibility,
  setTrimStatus,
  resetDownloadUrl,
  toggleSummaryEdit,
  toggleChatView,
  updateJobStatus,
  setTemplateOutput,
  setShowNotesOnly,
  setVideoMode,
  setOcrMode,
  addSelectedFile,
  removeSelectedFile,
  clearSelectedFiles,
  clearChatError,
  clearChatConversation,
  addUserMessageToChat,
  addAssistantMessageToChat,
  updateLastAssistantMessage,
  setChatStatus,
  setChatSessionId,
  truncateConversation,
  resetSelectedVideo,
  updateFileDataWithoutLoading,
  toggleSelectionMode,
  toggleSegmentSelection,
  clearSegmentSelection,
  selectAllSegments,
  setFileTag,
  setTranslatedSegments,
} = selectedVideoSlice.actions;
export default selectedVideoSlice.reducer;
